import theme_icons from '../../icons.js';
import { MediaUploaderGeneric } from '../../components/image-picker-generic.js';
import { AdderGeneric } from '../../components/adder-generic.js';

const { __ } = wp.i18n;
const { registerBlockType } = wp.blocks;
const { useEffect } = wp.element;
const { RichText, InspectorControls } = wp.blockEditor;
const { Button, PanelBody, TextControl, ToggleControl, RangeControl, RadioControl } = wp.components;

const reviewstop = {
  title: 'Review Columns',
  icon: theme_icons.greenheart,
  category: 'greenheart',
  attributes: {
    isAdding: {
      type: 'boolean',
      default: false
    },
    isEditing: {
      type: 'number',
      default: -1
    },
    title: {
      type: 'string',
      default: ''
    },

    image: {
      type: 'object',
      default: {}
    },
    reviewPercentage: {
      type: 'number',
      default: 0
    },
    numberOfReviews: {
      type: 'number',
      default: 0
    },
    starRating: {
      type: 'number',
      default: 0
    },
    linkText: {
      type: 'string',
      default: ''
    },
    url: {
      type: 'string',
      default: ''
    },
    isBlank: {
      type: 'boolean',
      default: false
    },
    columns: {
      type: 'array',
      default: []
    },
    currentId: {
      type: 'number',
      default: 0
    }
  },

  edit({ attributes, setAttributes }) {
    const { 
      isAdding, 
      isEditing, 
      title, 
      image, 
      reviewPercentage,
      numberOfReviews,
      starRating,
      linkText,
      url,
      isBlank,
      columns, 
      currentId 
    } = attributes;

    useEffect(() => {
      let i = getNextId();
      setAttributes({ currentId: i });
    }, []);

    const getNextId = () => {
      let i = 1;
      while (columns.filter((column) => column.id === i).length > 0) { i++; }
      return i;
    }

    function onTitleChange(newTitle) {
      setAttributes({ title: newTitle });
    }

    function onImageChange(newImage) {
      setAttributes({ image: newImage });
    }

    function onLinkTextChange(newLinkText) {
      setAttributes({ linkText: newLinkText });
    }

    function onUrlChange(newUrl) {
      setAttributes({ url: newUrl });
    }

    const resetAddColumnForm = () => {
      let nextId = getNextId();
      setAttributes({ 
        isAdding: false, 
        isEditing: -1, 
        title: '', 
        image: {}, 
        reviewPercentage: 0,
        numberOfReviews: 0,
        starRating: 0,
        linkText: '',
        url: ''
      });
    }

    const addColumn = () => {
      const nextId = getNextId();
      setAttributes({
        currentId: nextId,
        isAdding: true,
        isEditing: -1,
      });
    };

    const editColumn = (id) => {
      const column = columns.find((column) => column.id === id);
      setAttributes({
        title: column.title,
        image: column.image,
        reviewPercentage: column.reviewPercentage,
        numberOfReviews: column.numberOfReviews,
        starRating: column.starRating,
        linkText: column.linkText,
        url: column.url,
        isBlank: column.isBlank,
        currentId: id,
        isEditing: id,
      });
    };

    const saveColumn = () => {
      const newColumn = {
        title: title,
        image: image,
        reviewPercentage: reviewPercentage,
        numberOfReviews: numberOfReviews,
        starRating: starRating,
        linkText: linkText,
        url: url,
        isBlank: isBlank,
        id: currentId,
      };

      const columnIndex = columns.findIndex((column) => column.id === currentId);
      let newColumns;

      if (columnIndex !== -1) {
        newColumns = [...columns];
        newColumns[columnIndex] = newColumn;
      } else {
        newColumns = [...columns, newColumn];
      }

      setAttributes({ columns: newColumns });
      resetAddColumnForm();
    };

    const deleteColumn = (id) => {
      const newColumns = columns.filter((column) => column.id !== id);
      setAttributes({ columns: newColumns });
    }

    const displayColumns = () => {
      return (
        <div className="columns row">
          {columns.map((column, index) => {
            return (
              <div key={index} className="col">
                <div className="block column">
                  <div className="column-img-top">
                    <img className="column-image" src={column.image.mediaUrl} alt={column.title ?? 'card logo' + " image"} />
                  </div>
                  <div className="column-body">
                    <div className="review-stats">
                      <div className="percentage">{column.reviewPercentage}%</div>
                      <div className="reviews-count">{column.numberOfReviews} reviews</div>
                      <div className="star-rating">{column.starRating} stars</div>
                    </div>
                    {column.url && 
                      <a href={column.url} target={column.isBlank ? "_blank" : "_self"} rel="noopener">
                        {column.linkText}
                      </a>
                    }
                  </div>
                  <div className="column-controls">
                    <Button className="button secondary" onClick={() => deleteColumn(column.id)}>Delete</Button>
                    <Button className="button primary" onClick={() => editColumn(column.id)}>Edit</Button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    return ([
      <InspectorControls>
        <PanelBody title="Review Column Settings">
          <RangeControl
            label="Review Percentage"
            value={reviewPercentage}
            onChange={(value) => setAttributes({ reviewPercentage: value })}
            min={1}
            max={100}
          />
          <RangeControl
            label="Number of Reviews"
            value={numberOfReviews}
            onChange={(value) => setAttributes({ numberOfReviews: value })}
            min={0}
            max={1000}
            step={1}
          />
          <RangeControl
            label="Star Rating"
            value={starRating}
            onChange={(value) => setAttributes({ starRating: value })}
            min={0}
            max={5}
            step={0.5}
          />
          {url && 
            <ToggleControl
              label="Open link in a new tab"
              checked={isBlank}
              onChange={(newValue) => setAttributes({ isBlank: newValue })}
            />
          }
        </PanelBody>
      </InspectorControls>,
      <div className="rbt-review-columns">
        {(isAdding || isEditing > 0) ?
          <div className="rbt-review-columns-form">
            <div className="column-wrap">
              <div className="image-area">
                <MediaUploaderGeneric photo={image} updateImage={onImageChange} />
              </div>
              <div className="title-area">
                <RichText
                  tagName="h3"
                  className="column-title"
                  value={title}
                  onChange={onTitleChange}
                  placeholder="Review Title"
                />
              </div>
              <div className="link-area">
                <TextControl
                  label="Link Text"
                  value={linkText}
                  onChange={onLinkTextChange}
                />
                <TextControl
                  label="URL"
                  value={url}
                  onChange={onUrlChange}
                />
              </div>
            </div>
            {image.hasOwnProperty('mediaUrl') && 
              <Button className="button primary" onClick={saveColumn}>Save Column</Button>
            }
            <Button className="button secondary" onClick={resetAddColumnForm}>Cancel</Button>
          </div>
          :
          <div className="rbt-review-columns-columns">
            {displayColumns()}
            <AdderGeneric onAdd={addColumn} />
          </div>
        }
      </div>
    ]);
  },

  save({ attributes }) {
    const { columns } = attributes;
    const chevronRight = () => {
      return (
        <span className="chevron-right">
          <svg width="410" height="450" viewBox="0 0 410 450" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M247.6 202.4C260.1 214.9 260.1 235.2 247.6 247.7L55.6 439.7C43.1 452.2 22.8 452.2 10.3 439.7C-2.20001 427.2 -2.20001 406.9 10.3 394.4L179.7 225L10.4 55.6C-2.10001 43.1 -2.10001 22.8 10.4 10.3C22.9 -2.19995 43.2 -2.19995 55.7 10.3L247.7 202.3L247.6 202.4Z" fill="currentColor" />
            <path d="M400.6 202.4C413.1 214.9 413.1 235.2 400.6 247.7L208.6 439.7C196.1 452.2 175.8 452.2 163.3 439.7C150.8 427.2 150.8 406.9 163.3 394.4L332.7 225L163.4 55.6C150.9 43.1 150.9 22.8 163.4 10.3C175.9 -2.19995 196.2 -2.19995 208.7 10.3L400.7 202.3L400.6 202.4Z" fill="currentColor"/>
          </svg>
        </span>
      );
    }
    const fullStar = () => {
      return (
        <span className="full-star">
          <svg width="529" height="512" viewBox="0 0 529 512" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M292.9 18C287.6 7 276.4 0 264.1 0C251.8 0 240.7 7 235.3 18L171 150.3L27.4 171.5C15.4 173.3 5.4 181.7 1.7 193.2C-2 204.7 0.999997 217.4 9.6 225.9L113.8 329L89.2 474.7C87.2 486.7 92.2 498.9 102.1 506C112 513.1 125.1 514 135.9 508.3L264.2 439.8L392.5 508.3C403.3 514 416.4 513.2 426.3 506C436.2 498.8 441.2 486.7 439.2 474.7L414.5 329L518.7 225.9C527.3 217.4 530.4 204.7 526.6 193.2C522.8 181.7 512.9 173.3 500.9 171.5L357.2 150.3L292.9 18Z" fill="#FEDB00"/>
          </svg>
        </span>
      )
    }
    const halfStar = () => {
      return(
        <span className="half-star">
          <svg width="410" height="512" viewBox="0 0 410 512" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M264 0C251.8 0.1 240.7 7 235.4 18L171 150.3L27.4 171.5C15.4 173.3 5.4 181.7 1.7 193.2C-2 204.7 0.999997 217.4 9.6 225.9L113.8 329L89.2 474.7C87.2 486.7 92.2 498.9 102.1 506C112 513.1 125.1 514 135.9 508.3L264 439.8V0ZM405.9 512C407 512.1 408 512.1 409.1 512H405.9Z" fill="#FEDB00"/>
          </svg>
        </span>
      )
    }
    const renderStarRating = (rating) => {
      let stars = [];
      while(rating >= 1) {
        stars.push(fullStar());
        rating--;
      }
      if(rating > 0) {
        stars.push(halfStar());
      }
      
      return (stars);
    }

    return (
      <div className="block">
        <div className="columns row">
          {columns.map((column, index) => (
            <div key={index} className="col">
              <div className="column review-column">
                <div className="column-img-top">
                  <img alt={column.title ?? 'card logo'} loading="lazy" src={column.image.mediaUrl} />
                </div>
                <div className="column-body">
                  <div className="review-stats">
                    <div className="percentage">{column.reviewPercentage}%</div>
                    <div className="reviews-count">{column.numberOfReviews} reviews</div>
                    <div className="star-rating">
                      {renderStarRating(column.starRating)}
                    </div>
                  </div>
                  {column.url && 
                    <a 
                      href={column.url}
                      className="review-link"
                      target={column.isBlank ? "_blank" : "_self"}
                      rel="noopener"
                    >
                     {column.linkText} {chevronRight()}
                    </a>
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
};

registerBlockType('rbt/reviewstop', reviewstop);
export default reviewstop;